import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from '../../contexts/LanguageContext';

/**
 * Navbar language switcher — Phase 18h.
 *
 * A small dropdown that sits left of the notification bell. Opens
 * a 7-row menu of supported languages (labelled in their native
 * script so a user recognises the one they want even if the app
 * currently renders in a different language).
 *
 * On select:
 *   * context is updated synchronously → every ``useTranslation``
 *     consumer re-renders immediately.
 *   * the backend profile is PATCHed so the next email/SMS comes
 *     through in the picked language too.
 */
const LanguageSwitcher = () => {
  const { language, setLanguage, languages, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const current = languages.find((l) => l.code === language) || languages[0];

  const handlePick = async (code) => {
    if (code === language) { setOpen(false); return; }
    const picked = languages.find((l) => l.code === code);
    setSaving(true);
    try {
      await setLanguage(code);
      toast.success(
        t('prefs.language.set', 'Language set to {label}', {
          label: picked?.label || code,
        }),
      );
    } catch {
      toast.error(t('prefs.language.updateFailed', 'Failed to update language'));
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <a
        className="iq-waves-effect d-flex align-items-center"
        href="#language"
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t('nav.language', 'Language')}
        style={{ gap: 6 }}
      >
        <i className="ri-translate-2" />
        <span
          className="d-none d-md-inline"
          style={{ fontSize: '0.85rem', fontWeight: 500, lineHeight: 1 }}
        >
          {current.short}
        </span>
      </a>
      {open ? (
        <div
          role="listbox"
          aria-label={t('nav.language', 'Language')}
          className="iq-sub-dropdown"
          style={{
            display: 'block',
            position: 'absolute',
            top: '100%',
            right: 0,
            left: 'auto',
            transform: 'none',
            width: 220,
            zIndex: 1050,
          }}
        >
          <div className="iq-card shadow-none m-0">
            <div className="iq-card-body p-0">
              <div className="bg-light px-3 py-2 border-bottom">
                <small className="text-muted fw-semibold text-uppercase">
                  {t('nav.language', 'Language')}
                </small>
              </div>
              {languages.map((lang) => {
                const active = lang.code === language;
                return (
                  <button
                    key={lang.code}
                    type="button"
                    className="w-100 text-start px-3 py-2 border-0 d-flex align-items-center justify-content-between"
                    onClick={() => handlePick(lang.code)}
                    disabled={saving}
                    style={{
                      background: active ? 'rgba(13,110,253,.08)' : 'transparent',
                      color: active ? '#0d6efd' : '#212529',
                      fontWeight: active ? 600 : 400,
                      cursor: saving ? 'wait' : 'pointer',
                    }}
                  >
                    <span>{lang.label}</span>
                    {active ? <i className="ri-check-line" /> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LanguageSwitcher;
